import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "What's Included", href: "#included" },
  { label: "Website Examples", href: "#demos" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (hash) => (e) => {
    setOpen(false);
    if (!onHome) return; // let default link behavior take user to "/#hash"
    const el = document.querySelector(hash);
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", hash);
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full backdrop-blur-md transition-colors ${
        scrolled
          ? "bg-white/90 border-b border-goc-border"
          : "bg-white/80 border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link">
          <Logo variant="dark" />
        </Link>

        <ul className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={onHome ? l.href : `/${l.href}`}
                onClick={scrollTo(l.href)}
                className="text-sm font-medium text-goc-text/80 hover:text-goc-primary transition-colors"
                data-testid={`nav-link-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <a
            href={onHome ? "#lead-form" : "/#lead-form"}
            onClick={scrollTo("#lead-form")}
            className="inline-flex items-center rounded-lg bg-goc-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-goc-deep transition-colors"
            data-testid="nav-cta-get-my-website"
          >
            Get My Website
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-goc-text hover:bg-goc-light transition-colors"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open ? (
        <div
          id="mobile-menu"
          className="lg:hidden border-t border-goc-border bg-white"
          data-testid="nav-mobile-menu"
        >
          <ul className="px-4 py-3 space-y-1">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={onHome ? l.href : `/${l.href}`}
                  onClick={scrollTo(l.href)}
                  className="block rounded-md px-3 py-3 text-base font-medium text-goc-text hover:bg-goc-light transition-colors"
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-2">
              <a
                href={onHome ? "#lead-form" : "/#lead-form"}
                onClick={scrollTo("#lead-form")}
                className="block rounded-lg bg-goc-primary px-5 py-3 text-center text-base font-semibold text-white hover:bg-goc-deep transition-colors"
                data-testid="nav-mobile-cta-get-my-website"
              >
                Get My Website
              </a>
            </li>
          </ul>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;
